import { APP_ENVIRONMENT } from '@config/env';
import {
  getUniqueChatWindowId,
  setUniqueChatWindowId,
} from '@services/sessionStorageService';
import type React from 'react';
import { Component } from 'react';
import Bubble from '../containers/Bubble';
import ChatWindow from '../containers/ChatWindow';
import { ConfigContext } from '../context/ConfigContext';
import { getChatState, setChatState } from '../services/chatStateService';
import * as eventHandlerService from '../services/eventHandlerService';
import languageService from '../services/languageService';
import { getUserId } from '../services/localStorageService';
import { sendUpdateWindowType } from '../services/postMessageService';
import querystringService from '../services/querystringService';
import {
  getMessagesOfUser,
  getMessageTimestamp,
} from '../services/userService';
import type { AppProps, AppState } from '../types/App';
import type {
  Button,
  ChatActivityStatus,
  ChatHistory,
  Message,
  MessageDirection,
  MessagePayload,
} from '../types/ChatWindow';
import type { ChatWindowState, InitStateProps } from '../types/configs';
import Greeting from './Greeting';

class App extends Component<AppProps, AppState> {
  static contextType = ConfigContext;
  context!: React.ContextType<typeof ConfigContext>;
  registeredEvents = {
    set: () => this.setState({ registeredEvents: true }),
    get: () => {
      const { registeredEvents } = this.state;
      return registeredEvents;
    },
    delete: () => this.setState({ registeredEvents: false }),
  };

  lastFetchedTimestamp: number | undefined; // Track the last fetched message timestamp

  constructor(props: AppProps) {
    super(props);
    const {
      config: {
        options: { defaultState = 'closed', allowTextInput },
        configId,
      },
    } = props;

    const windowType =
      getChatState(configId) === 'closed' ? 'closed' : defaultState;

    // Fullscreen mode should open immediately without waiting for componentDidMount
    const toggleState = windowType === 'fullscreen';

    this.initState({
      allowTextInput,
      windowType: windowType,
      windowToggle: toggleState,
      isUserFullscreen: false, // Track user-triggered fullscreen
    });
  }

  handleFullscreenChange = (isFullscreen: boolean) => {
    this.setState({ isUserFullscreen: isFullscreen });
  };

  shouldShowBubble = () => {
    const { windowType, isUserFullscreen, windowToggle } = this.state;

    // Config-based fullscreen: hide bubble only while the window is open;
    // show it once the user closes the fullscreen window so they can reopen it.
    if (windowType === 'fullscreen') {
      return !windowToggle;
    }

    // User-triggered fullscreen hides the bubble while the window is open
    const isUserFullscreenActive = isUserFullscreen && windowToggle;
    return !isUserFullscreenActive;
  };

  // Ensure the chat window starts in the appropriate state
  ensureWindowTypeIsOpen = () => {
    const {
      config: { configId },
    } = this.props;
    const { windowType } = this.state;

    const chatState = getChatState(configId);

    if (
      chatState !== 'closed' &&
      !['fullscreen', 'open'].includes(windowType)
    ) {
      this.setState({ windowType: 'open' });
    }
  };

  fetchMessagesForId = async () => {
    const {
      config: { configId },
    } = this.props;
    const id = getUserId(configId);

    if (!id) {
      return;
    }

    const { newMessages, timestamp } = await this.getNewMessages(id);

    if (newMessages.length > 0) {
      this.setState((prevState) => {
        return {
          // * If timestamp is defined, it means we are fetching new messages after the last fetched timestamp
          // * If timestamp is not defined, it means we are fetching the whole chat history
          // * In that case, we should replace the previous messages with the new ones to avoid duplicates
          messages: timestamp
            ? [...prevState.messages, ...newMessages]
            : newMessages,
        };
      });

      this.ensureWindowTypeIsOpen();
    }
  };

  /**
   * This solution was implemented because messages are fetched when the web client mounts,
   * but the chat window might not be open at that time (ex: when the chat state is 'closed').
   * In such cases, the WebSocket connection is not initialized until the chat window is opened for the first time.
   *
   * If the WebSocket is not initialized and messages have already been fetched from the server,
   * any messages sent by the other party before the WebSocket initialization would not be displayed.
   * This method solves that problem by ensuring that new messages are fetched and displayed
   * even if they were sent before the WebSocket connection was established.
   */
  getNewMessages = async (id: string) => {
    const lastTimestamp = this.lastFetchedTimestamp;

    // Fetch messages from the server, passing the last fetched timestamp
    const chatHistory = await getMessagesOfUser(id, lastTimestamp);

    if (chatHistory.length > 0) {
      this.lastFetchedTimestamp = getMessageTimestamp(
        chatHistory[chatHistory.length - 1],
      );
    }

    return {
      newMessages: this.convertChatHistoryToMessages(chatHistory),
      timestamp: lastTimestamp, // this is the timestamp that used to fetch the messages
    };
  };

  enableChat = async () => {
    const {
      config: {
        options: { allowTextInput: allowTextInputDefault },
      },
    } = this.props;

    // TODO: Refactor
    const messages = this.state.messages;
    const lastMessagePayload = messages?.[messages.length - 1]?.payload;
    // Use navigator.onLine to check the actual network state rather than reading
    // React state, which may not yet reflect the current connection status.
    const isCurrentlyOffline = !navigator.onLine;

    if (isCurrentlyOffline) {
      // Preserve the offline banner and disabled input when re-enabling while
      // the connection is still down.
      this.setState({ chatEnabled: true });
    } else {
      this.setState({
        chatEnabled: true,
        bannerStatus: 'info',
        bannerMessage: '',
        // Reset allowTextInput as it does not seem to restart on its own
        allowTextInput: this.getTextInputStatus(
          lastMessagePayload,
          allowTextInputDefault,
        ),
      });
    }
    this.fetchMessagesForId();
  };

  componentDidMount = async () => {
    setUniqueChatWindowId();

    // Disable console logging in production and staging
    if (!['development', 'local'].includes(APP_ENVIRONMENT)) {
      console.log = () => {};
      console.debug = () => {};
      console.error = () => {};
      console.info = () => {};
    }

    querystringService.register(window.location.search);

    const { language } = this.context;
    languageService.registerLanguage(language);
    eventHandlerService.subscribeToEvent('new-webclient', (eventData) => {
      // This means that the chat was requested by this chat, and this must be enabled.
      // This also solves the problem that it was not possible to reactivate the blocked chat,
      // or if there were more than 2 browser tabs ( with same webclient ), it did not know which one to reactivate exactly.
      const uniqueChatWindowId = getUniqueChatWindowId();
      if (
        eventData?.requestSenderId &&
        eventData.requestSenderId === uniqueChatWindowId
      ) {
        this.setState({ chatEnabled: true });
      } else {
        this.setState({ chatEnabled: false });
        this.registeredEvents.delete();
      }
    });

    // Fetch messages and ensure the chat window starts in the correct state
    await this.fetchMessagesForId();

    this.showWindowIfNeeded();
  };

  componentDidUpdate = (_prevProps: AppProps, prevState: AppState) => {
    const { messages } = this.state;

    if (prevState.messages.length !== messages.length && messages.length > 0) {
      this.setAllowTextInputOnMessage(messages);
    }
  };

  // * Check if the text input should be visible or not
  getTextInputStatus = (
    messagePayload: MessagePayload | undefined,
    defaultAllowTextInput = true,
  ) => {
    // * Disable text input on dropdown
    if (messagePayload?.type === 'dropdown') {
      return false;
    }

    if (
      messagePayload &&
      'options' in messagePayload &&
      typeof messagePayload?.options?.webclient?.buttonInteractionOnly ===
        'boolean'
    ) {
      // if the buttonInteractionOnly is set to true, we should not allow text input ( send back 'false' input status )
      return !messagePayload?.options?.webclient?.buttonInteractionOnly;
    }

    if (defaultAllowTextInput) {
      // DEPRECATED: since we introduced the new buttonInteractionOnly field, this value is coming from the main webclient config instead of the block config.
      return defaultAllowTextInput;
    }

    if (
      messagePayload &&
      'options' in messagePayload &&
      typeof messagePayload?.options?.webclient?.allowTextInput === 'boolean' // DEPRECATED: since we introduced the new buttonInteractionOnly field, but because of the old configs, it has to be kept until they are rewritten.
    ) {
      return messagePayload?.options?.webclient?.allowTextInput;
    }

    return defaultAllowTextInput;
  };

  setAllowTextInputOnMessage = (messages: Message[]) => {
    const lastMessagePayload = messages?.[messages.length - 1]?.payload;

    const {
      config: {
        options: { allowTextInput: allowTextInputDefault },
      },
    } = this.props;

    this.setState((prevState) => ({
      ...prevState,
      allowTextInput: this.getTextInputStatus(
        lastMessagePayload,
        allowTextInputDefault,
      ),
    }));
  };

  showWindowIfNeeded = () => {
    const { popupTimeout } = this.context;
    const { windowType } = this.state;

    if (windowType === 'closed') {
      return;
    }

    if (windowType === 'fullscreen') {
      this.showWindow();
      return;
    }

    setTimeout(() => {
      this.showWindow();
    }, popupTimeout);
  };

  showWindow = () => {
    const { windowType } = this.state;
    this.setState({ windowToggle: true });
    this.sendWindowStatus({
      windowToggle: true,
      windowType: windowType,
    });
  };

  // TODO: Refactor
  // TODO: Consider showing a blocker overlay when core is unreachable on initial connect
  // (socket.io exhausts retries but never established a connection - currently input is
  // silently disabled with no user-facing explanation).
  toggleConnectionStateBanner = (status: boolean) => {
    const statusMessage = status
      ? 'Connectie hersteld'
      : 'Connectie onderbroken, tijdelijk offline';
    const statusType = status ? 'info' : 'error';
    const messages = this.state.messages;
    const lastMessagePayload = messages?.[messages.length - 1]?.payload;

    const {
      config: {
        options: { allowTextInput: allowTextInputDefault },
      },
    } = this.props;

    // * on connection change, update the banner message and status
    this.setState({ bannerMessage: statusMessage, bannerStatus: statusType });

    if (!status) {
      // * Only change allowTextInput on status === false
      this.setState({
        allowTextInput: false,
      });
    }

    // * If reconnected only show the banner temporarily
    if (status) {
      setTimeout(() => {
        this.setState({
          bannerMessage: '',
          bannerStatus: 'info',
          // * Return allowTextInput to it's original state, based on default and last message
          allowTextInput: this.getTextInputStatus(
            lastMessagePayload,
            allowTextInputDefault,
          ),
        });
      }, 1000 * 3);
    }
  };

  // Get the status of the chat window
  getWindowStatus = ({ windowToggle, windowType }) => {
    if (!windowToggle) {
      return 'closed';
    }
    return windowType;
  };

  // Send window status to the parent window via postMessage
  sendWindowStatus = ({ windowToggle, windowType }) => {
    const windowStatus = this.getWindowStatus({ windowToggle, windowType });
    sendUpdateWindowType(windowStatus);
  };

  handleMinimizeButton = (show = false) => {
    const { windowType, windowTimeout, windowToggle } = this.state;
    const {
      config: {
        configId,
        options: { defaultState = 'closed' },
      },
    } = this.props;

    if (windowTimeout) {
      clearTimeout(windowTimeout);
    }

    this.setState({
      windowTimeout: undefined,
    });

    if (windowType === 'closed') {
      const nextWindowType =
        getUserId(configId) || defaultState === 'closed'
          ? 'open'
          : defaultState;

      this.setState({ windowType: nextWindowType, windowToggle: true });
      this.sendWindowStatus({ windowToggle: true, windowType: nextWindowType });
      setChatState(configId, 'open');

      // Fetch new messages when opening the chat window
      this.fetchMessagesForId();

      return;
    }

    const windowToggleState = !windowToggle;

    // Reset isUserFullscreen when closing the window
    if (!windowToggleState) {
      this.setState({ isUserFullscreen: false });
    }

    setChatState(configId, windowToggle ? 'closed' : 'open');
    this.setState({ windowToggle: windowToggleState });
    this.sendWindowStatus({
      windowToggle: windowToggleState,
      windowType,
    });
  };

  onLaterButtonClick = () => {
    const { windowType } = this.state;
    const { config } = this.props;
    const timeout = config.options.greetingTimeout || 60000;
    this.setState({ windowToggle: false });
    this.setState({
      windowTimeout: setTimeout(() => {
        this.setState({ windowToggle: true });
        this.sendWindowStatus({
          windowToggle: true,
          windowType,
        });
      }, timeout),
    });
    this.sendWindowStatus({
      windowToggle: false,
      windowType: windowType,
    });
  };

  hideGreeting = () => {
    this.setState({ windowType: 'open', windowToggle: true });
    this.sendWindowStatus({ windowToggle: true, windowType: 'open' });
  };

  addMessage = (direction: MessageDirection, params: MessagePayload) => {
    // * Must use the updater form: the bot can emit two blocks in the same tick
    // * (an answer followed by its follow-up question). Reading this.state.messages
    // * here would give both calls the same stale array, and the second setState
    // * would drop the first message.
    this.setState((prevState) => ({
      messages: [...prevState.messages, { direction, payload: params }],
    }));

    // Update lastFetchedTimestamp when a new message is received via WebSocket
    // We use current time as the timestamp for real-time messages
    this.lastFetchedTimestamp = Date.now();
  };

  setMessages = async (id: string) => {
    const chatHistory = await getMessagesOfUser(id);
    const messages = this.convertChatHistoryToMessages(chatHistory);
    this.setState({ messages });

    // Update the lastFetchedTimestamp when loading messages
    if (chatHistory.length > 0) {
      this.lastFetchedTimestamp = getMessageTimestamp(
        chatHistory[chatHistory.length - 1],
      );
    }

    this.ensureWindowTypeIsOpen();
  };

  changeWindowState = (state: ChatWindowState) => {
    this.setState({ windowType: state });
  };

  changeChatActivityStatus = (status: ChatActivityStatus) => {
    this.setState({ chatActivityStatus: status });
  };

  changeFlowState = (state: string) => {
    this.setState({ flowState: state });
  };

  initState = (props: InitStateProps) => {
    const initialValues: AppState = {
      chatEnabled: true,
      allowTextInput: true, // DEPRECATED: since we introduced the new buttonInteractionOnly field, but because of the old configs, it has to be kept until they are rewritten.
      windowType: props.windowType,
      windowToggle: props.windowToggle,
      messages: [],
      registeredEvents: false,
      connectionStatus: true,
      bannerStatus: 'info',
      bubbleClickHandler: this.handleMinimizeButton,
      chatActivityStatus: 'online' as ChatActivityStatus,
      isUserFullscreen: props.isUserFullscreen ?? false,
      // Reset flow state on session reset so Bubble does not carry over a stale
      // 'handover' state into the next session.
      flowState: '',
    };

    // The state is not initialized yet, this is the first time the component is rendered
    if (!this.state) {
      this.state = initialValues;
      return;
    }

    // The state is already initialized, we need to update the re-initialized/reset values
    this.setState(initialValues);
  };

  isValidEntryBlockTypeByDirection = (direction: string, type: string) => {
    switch (direction) {
      case 'in': {
        return ['text', 'button', 'image'].indexOf(type) > -1;
      }
      case 'out': {
        return (
          [
            'text',
            'text-v2',
            'button',
            'textWithButtons',
            'imageWithButtons',
            'databaseUpdate',
            'imageCarousel',
            'feedback',
          ].indexOf(type) > -1
        );
      }
      case 'system': {
        return type === 'system';
      }
      default:
        return false;
    }
  };

  convertChatHistoryToMessages = (history: ChatHistory[]): Message[] => {
    // TODO: Move filter to backend
    const filteredHistory =
      history?.filter((entry) =>
        this.isValidEntryBlockTypeByDirection(
          entry.direction,
          entry.messageType,
        ),
      ) ?? [];

    const parsedHistory = filteredHistory
      ?.map((entry, index) => {
        return {
          direction: entry.direction,
          payload: this.determinePayload(entry, index, filteredHistory),
        };
      })
      // * Do one final filter in case the data is incorrect (payload is null)
      ?.filter((message): message is Message => Boolean(message.payload));

    return parsedHistory;
  };

  getTextFromButton = (data) => {
    if (data?.formattedValue) {
      return data.formattedValue;
    }
    if (data) {
      const { content } = data;
      if (typeof content === 'object') {
        if (content.title) {
          return content.title;
        }
      }
      return content;
    }
    return '';
  };

  isURLButton = (buttons: Button[]) => {
    return buttons?.[0]?.url || buttons?.[0]?.isURLButton;
  };

  determinePayload = (
    data: ChatHistory,
    index: number,
    allHistory: any[],
  ): MessagePayload | null => {
    const allHistoryLength = allHistory.length;

    switch (data.messageType) {
      case 'text': {
        return {
          type: 'text',
          data: {
            text: data.message.content,
            replaceTextUrl: data.message.replaceTextUrl,
          },
          options: {
            ...(data?.message?.options || {}),
            ...(data?.options || {}),
          },
        };
      }
      case 'image': {
        return { type: 'image', data: { url: data.message.content.url } };
      }
      case 'textWithButtons':
      case 'text-v2': {
        // * in case the payload is incorrect send back null
        if (!data.content) {
          return null;
        }

        // * If the last message is a text with buttons, we should show the buttons ( like as a quick reply, menu buttons )
        // * Or if the button is a URL button, we should show the buttons always (even if it is not the last message) because the user can click on it anytime
        return index + 1 === allHistoryLength ||
          this.isURLButton(data.content?.buttons)
          ? {
              type: 'textWithButtons',
              data: {
                text: data.content.text,
                buttons: data.content?.buttons ?? [],
                replaceTextUrl: data.content.replaceTextUrl,
                ...(data?.content?.llmUsageId && {
                  llmUsageId: data.content.llmUsageId,
                }),
              },
              options: data.options,
            }
          : {
              type: 'text',
              data: {
                text: data.content.text,
                replaceTextUrl: data.content.replaceTextUrl,
              },
              options: data?.options || {},
            };
      }
      case 'imageCarousel': {
        return {
          type: 'carousel',
          data: { ratio: 'square', elements: data.content },
          options: data?.options,
        };
      }
      case 'button': {
        return {
          type: 'text',
          data: {
            text: this.getTextFromButton(data),
          },
        };
      }
      case 'credential': {
        return { type: 'text', data: { text: 'Authenticated' } };
      }
      case 'feedback': {
        return {
          type: 'feedback',
          data: {
            submitText: data.content.submitText,
            minRating: data.content.minRating,
            maxRating: data.content.maxRating,
            rating: data.content.rating,
          },
        };
      }
      case 'system': {
        return {
          type: 'system',
          data: {
            text: data?.message?.content,
          },
        };
      }
      default: {
        throw Error('Invalid message type!');
      }
    }
  };

  render() {
    const {
      chatEnabled,
      allowTextInput,
      windowToggle,
      messages,
      bubbleClickHandler,
      windowType,
      chatActivityStatus,
      bannerStatus,
      bannerMessage,
      isUserFullscreen,
      flowState,
    } = this.state;

    const { config } = this.props;

    const showBubble = this.shouldShowBubble();

    // * We enable/init WebSocket connection only when the chat is in an open state when the user has consented to chat with the bot (by clicking the start button or opening the chat bubble).
    // * This approach frees up resources for users who choose not to interact.
    const shouldInitWebSocket = ['open', 'fullscreen'].includes(windowType);

    return (
      <div className='main'>
        <ChatWindow
          onClose={this.handleMinimizeButton}
          chatEnabled={chatEnabled}
          onEnableChat={this.enableChat}
          changeWindowState={this.changeWindowState}
          changeChatActivityStatus={this.changeChatActivityStatus}
          chatActivityStatus={chatActivityStatus}
          sendWindowStatus={this.sendWindowStatus}
          allowTextInput={allowTextInput}
          fullscreen={windowType === 'fullscreen'}
          messages={messages}
          addMessage={this.addMessage}
          registeredEvents={this.registeredEvents}
          setMessages={this.setMessages}
          initState={this.initState}
          data={config.data}
          initWebSocket={shouldInitWebSocket}
          toggleConnectionStateBanner={this.toggleConnectionStateBanner}
          bannerStatus={bannerStatus}
          bannerMessage={bannerMessage}
          hidden={!windowToggle || !shouldInitWebSocket}
          isUserFullscreen={isUserFullscreen}
          onFullscreenChange={this.handleFullscreenChange}
          onFlowStateChange={this.changeFlowState}
        />
        {windowToggle && windowType === 'greeting' && (
          <Greeting
            onClose={this.handleMinimizeButton}
            onButtonClick={this.hideGreeting}
            onLaterClick={this.onLaterButtonClick}
          />
        )}
        {showBubble && (
          <Bubble
            changeChatActivityStatus={this.changeChatActivityStatus}
            clickHandler={bubbleClickHandler}
            windowType={windowType}
            windowToggle={windowToggle}
            flowState={flowState}
          />
        )}
      </div>
    );
  }
}

export default App;
